import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Loader2, Lock, Shield, Gift, Check, Sparkles, Volume2, VolumeX } from "lucide-react";
import api, { formatError } from "@/lib/api";
import Avatar from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { playUnlockChime, isMuted, setMuted } from "@/lib/sfx";

const CONFETTI_COLORS = ["#FACC15", "#10B981", "#3B82F6", "#EC4899", "#F59E0B", "#8B5CF6"];

function Celebration({ avatar, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="avatar-celebration"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-6">
      {/* confetti */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(40)].map((_, i) => (
          <motion.div key={i}
            initial={{ y: -40, x: `${Math.random() * 100}%`, opacity: 1, rotate: 0 }}
            animate={{ y: "110vh", rotate: 360 + Math.random() * 360, opacity: [1, 1, 0.8] }}
            transition={{ duration: 1.8 + Math.random() * 1.2, delay: Math.random() * 0.5, ease: "easeIn" }}
            className="absolute top-0"
            style={{ width: 10, height: 14, background: CONFETTI_COLORS[i % CONFETTI_COLORS.length], borderRadius: 2 }}
          />
        ))}
      </div>
      <motion.div
        initial={{ scale: 0.5, rotate: -8, opacity: 0 }}
        animate={{ scale: 1, rotate: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 16 }}
        className="relative nb-card bg-white p-8 text-center max-w-sm w-full">
        <p className="font-parent font-bold text-amber-600 text-sm uppercase tracking-wide">New avatar unlocked!</p>
        <motion.div
          animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 2 }}
          className="my-5 flex justify-center">
          <Avatar avatar={avatar.id} img={avatar.img} size={128} testId="celebration-avatar" />
        </motion.div>
        <h2 className="font-kids text-3xl font-bold text-slate-900">{avatar.name}</h2>
        <p className="font-parent text-slate-500 mt-1">joined your collection! 🎉</p>
        <Button onClick={onClose} data-testid="celebration-close"
          className="mt-6 rounded-full bg-slate-900 hover:bg-slate-800 text-white h-12 px-8 border-2 border-slate-900 font-parent font-semibold">
          Awesome!
        </Button>
      </motion.div>
    </motion.div>
  );
}

export default function AvatarCollection() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [celebrate, setCelebrate] = useState(null);
  const [muted, setMutedState] = useState(isMuted());

  const toggleSound = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
    if (!next) playUnlockChime();
  };

  const load = () =>
    api.get(`/children/${childId}/collection`).then(({ data }) => setData(data)).catch(() => navigate(`/kid/${childId}`));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [childId]);

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#EAF7F4]"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;
  }

  const { child, avatars, available_unlocks, golden_chapters, chapters_per_unlock, next_unlock_in } = data;

  const unlock = async (id) => {
    setBusy(id);
    try {
      await api.post(`/children/${childId}/collection/unlock`, { avatar_id: id });
      const a = (data?.avatars || []).find((x) => x.id === id);
      if (a) setCelebrate(a);
      playUnlockChime();
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not unlock this avatar.");
    } finally { setBusy(null); }
  };

  const wear = async (id) => {
    setBusy(id);
    try {
      await api.post(`/children/${childId}/avatar`, { avatar_id: id });
      toast.success("Avatar changed!");
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not change avatar.");
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-[#EAF7F4] font-kids text-slate-900">
      <AnimatePresence>
        {celebrate && <Celebration avatar={celebrate} onClose={() => setCelebrate(null)} />}
      </AnimatePresence>
      <header className="max-w-5xl mx-auto px-5 pt-6 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(`/kid/${childId}`)} className="rounded-full font-parent" data-testid="collection-back">
          <ArrowLeft size={18} className="mr-1" /> Back
        </Button>
        <button
          onClick={toggleSound}
          data-testid="collection-sound-toggle"
          title={muted ? "Sound off" : "Sound on"}
          className="inline-flex items-center justify-center w-11 h-11 rounded-full border-4 border-slate-900 bg-white"
          style={{ boxShadow: "3px 3px 0 0 #0F172A" }}>
          {muted ? <VolumeX size={20} className="text-slate-400" /> : <Volume2 size={20} className="text-blue-600" />}
        </button>
      </header>

      <div className="max-w-5xl mx-auto px-5 pt-4">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="nb-card bg-white p-6 sm:p-7 flex flex-col sm:flex-row items-center gap-5">
          <Avatar avatar={child.avatar} img={child.avatar_img} size={88} testId="collection-active-avatar" />
          <div className="flex-1 text-center sm:text-left">
            <h1 className="text-2xl sm:text-3xl font-bold">My Collection</h1>
            <p className="font-parent text-slate-500 mt-1">
              {child.collected_count} friend{child.collected_count === 1 ? "" : "s"} collected
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-2xl border-4 border-slate-900 bg-blue-50 px-4 py-3" style={{ boxShadow: "3px 3px 0 0 #0F172A" }}>
            <Shield className="text-blue-600" size={22} />
            <div className="text-left">
              <p className="text-xl font-bold leading-none" data-testid="collection-golden">{golden_chapters}</p>
              <p className="font-parent text-xs text-slate-500">chapters mastered</p>
            </div>
          </div>
        </motion.div>

        {/* Unlock status banner */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className={`nb-card mt-5 p-5 flex items-center gap-3 ${available_unlocks > 0 ? "bg-amber-300" : "bg-white"}`} data-testid="collection-unlock-banner">
          <div className={`w-12 h-12 rounded-full border-4 border-slate-900 flex items-center justify-center shrink-0 ${available_unlocks > 0 ? "bg-white" : "bg-slate-100"}`}>
            <Gift className={available_unlocks > 0 ? "text-amber-600" : "text-slate-400"} size={24} />
          </div>
          {available_unlocks > 0 ? (
            <p className="font-parent font-semibold text-slate-900">
              You have <span className="font-bold">{available_unlocks}</span> avatar unlock{available_unlocks === 1 ? "" : "s"}! Tap a locked friend below to add it to your collection.
            </p>
          ) : (
            <p className="font-parent text-slate-600">
              Master <span className="font-bold text-slate-900">{next_unlock_in}</span> more chapter{next_unlock_in === 1 ? "" : "s"} (score 80%+ on all 3 levels) to unlock a new avatar. Every {chapters_per_unlock} mastered chapters = 1 new friend!
            </p>
          )}
        </motion.div>

        {/* Grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4 py-7">
          {avatars.map((a, i) => {
            const canUnlock = !a.owned && !a.holiday && available_unlocks > 0;
            return (
              <motion.div key={a.id}
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: Math.min(i * 0.02, 0.4) }}
                className={`nb-card ${a.holiday ? "bg-orange-50" : "bg-white"} p-3 flex flex-col items-center gap-2 relative ${a.active ? "ring-4 ring-green-400" : ""}`}
                data-testid={`avatar-card-${a.id}`}>
                <div className="relative">
                  <div className={a.owned ? "" : "opacity-90"}>
                    <Avatar avatar={a.id} img={a.img} size={72} />
                  </div>
                  {!a.owned && (
                    <div className="absolute -bottom-1 -left-1 w-7 h-7 rounded-full bg-slate-900 border-2 border-white flex items-center justify-center">
                      <Lock size={13} color="#fff" />
                    </div>
                  )}
                  {a.active && (
                    <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-green-500 border-2 border-white flex items-center justify-center">
                      <Check size={14} color="#fff" strokeWidth={3} />
                    </div>
                  )}
                </div>
                <p className="font-parent text-xs font-semibold text-center leading-tight truncate w-full">{a.name}</p>
                {a.owned ? (
                  a.active ? (
                    <span className="text-[11px] font-parent font-bold text-green-600">Wearing</span>
                  ) : (
                    <button onClick={() => wear(a.id)} disabled={busy === a.id} data-testid={`avatar-wear-${a.id}`}
                      className="text-[11px] font-parent font-bold text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-3 py-1 hover:bg-blue-100 disabled:opacity-50">
                      Wear
                    </button>
                  )
                ) : canUnlock ? (
                  <button onClick={() => unlock(a.id)} disabled={busy === a.id} data-testid={`avatar-unlock-${a.id}`}
                    className="text-[11px] font-parent font-bold text-slate-900 bg-amber-300 border-2 border-slate-900 rounded-full px-3 py-1 hover:bg-amber-400 disabled:opacity-50 inline-flex items-center gap-1">
                    <Sparkles size={11} /> Unlock
                  </button>
                ) : a.holiday ? (
                  <span className="text-[11px] font-parent font-bold text-orange-600 text-center leading-tight" data-testid={`avatar-holiday-${a.id}`}>Finish the Holiday Pack</span>
                ) : (
                  <span className="text-[11px] font-parent text-slate-400">Locked</span>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
